<template>
  <div class="card shadow-sm dashboard-card">
    <div class="card-header header-amethyst d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center gap-2">
        <h6 class="mb-0 p-1 text-white">Envio de Avaliações de Desempenho</h6>
        <!-- <span class="badge badge-on-amethyst">Pendentes: {{ total }}</span> -->
      </div>

      <div class="d-flex align-items-center gap-2 header-controls">
        <div class="input-group input-group-sm" style="width: 130px;">
          <span class="input-group-text">Lote</span>
          <input type="number" class="form-control"
                min="0" max="30"
                v-model.number="loteTamanho"
                @change="onChangeLote" />
        </div>

        <button
          class="btn btn-sm btn-outline-white"
          :disabled="!permissaoEnviar || selectedCount === 0 || sendingBatch"
          @click="enviarLote"
        >
          <template v-if="sendingBatch">
            <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Enviando… ({{ Math.min(batchDone + 1, batchTotal) }}/{{ batchTotal || selectedCount }})
          </template>
          <template v-else>
            Enviar em lote ({{ selectedCount }})
          </template>
        </button>
      </div>
    </div>

    <div class="card-body p-0">
      <div v-if="error" class="alert alert-danger rounded-0 m-0">
        {{ error }}
      </div>

      <div v-if="loading" class="p-3 text-center text-muted small">
        Carregando pendências…
      </div>

      <div v-else>
        <div class="table-responsive">
          <table class="table table-sm table-striped table-hover align-middle mb-0">
            <thead>
              <tr>
                <th style="width: 36px;" class="text-center">
                  <input type="checkbox" class="chk-sm" :checked="allChecked" @change="toggleAllPage" />
                </th>
                <th style="width: 140px;">Matrícula</th>
                <th>Aluno</th>
                <th>Avaliação</th>
                <th style="width: 90px;" class="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="rows.length === 0">
                <td colspan="5" class="text-center text-muted py-4">
                  Nenhuma avaliação pendente de envio.
                </td>
              </tr>

              <tr v-for="r in rows" :key="r.resposta_id">
                <td class="text-center">
                  <input type="checkbox"
                        class="chk-sm"
                        :checked="isSelected(r.resposta_id)"
                        @change="toggleSelected(r.resposta_id, $event.target.checked)" />
                </td>
                <td>
                  <span class="mono" :class="{ 'text-danger': r.matricula_vigente === false }">
                    {{ r.matricula_id }}
                  </span>
                </td>
                <td>{{ r.aluno }}</td>
                <td>
                  <span class="text-truncate d-inline-block" style="max-width: 380px;"
                        :title="`${r.avaliacao} — Realizada em ${fmtDate(r.data_realizacao)}`">
                    {{ r.avaliacao }}
                  </span>
                </td>
                <td class="text-center actions">
                  <!-- Pré-visualizar: abre o modal -->
                  <i 
                    class="bi bi-envelope"
                    style="cursor:pointer; color:#9B59B6;"
                    title="Enviar e-mail de avaliações"
                    role="button"
                    tabindex="0"
                    @click="preVisualizar(r)"
                    @keyup.enter="preVisualizar(r)"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginação -->
        <div class="card-pagination d-flex justify-content-between align-items-center">
          <div class="small pagination-text">
            Página {{ paginaAtual }} de {{ totalPaginas }} • Exibindo {{ rows.length }} de {{ total }}
          </div>
          <div class="btn-group">
            <button class="btn btn-outline-amethyst btn-compact"
                    :disabled="paginaAtual === 1 || loading"
                    @click="paginaAnterior">
              <i class="bi bi-chevron-left"></i>
            </button>
            <button class="btn btn-outline-amethyst btn-compact"
                    :disabled="paginaAtual >= totalPaginas || loading"
                    @click="proximaPagina">
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div v-if="showPreview" class="modal-overlay" @click.self="showPreview = false">
      <div class="modal-container">
        <div class="modal-header">
          <h6 class="mb-0">Pré-visualização do E-mail de Avaliação</h6>
          <button type="button" class="btn-close" aria-label="Fechar" @click="showPreview = false"></button>
        </div>

        <div class="modal-body">
          <div v-if="previewLoading" class="text-muted">Carregando…</div>
          <div v-else-if="previewError" class="alert alert-danger">{{ previewError }}</div>
          <div v-else-if="preview">
            <div class="mb-2 small text-muted">
              <strong>Aluno:</strong> {{ preview.aluno }} •
              <strong>Matrícula:</strong>
              <span :class="{ 'text-danger': preview.matricula_vigente === false }">
                {{ preview.matricula_id }}
              </span>
              • <strong>Responsável:</strong> {{ preview.responsavel_nome }} ({{ preview.responsavel_email || '—' }})<br>
              <strong>Avaliação:</strong> {{ preview.avaliacao }} •
              <strong>Data:</strong> {{ fmtDate(preview.data_realizacao) }} •
              <strong>Instrutor:</strong> {{ preview.instrutor_nome || '—' }}
            </div>

            <div class="border rounded p-2 bg-light" style="max-height: 60vh; overflow:auto;">
              <!-- Cabeçalho vindo do 'Documentos' -->
              <div class="mb-3" v-safe-html="preview.cabecalho_html"></div>

              <div class="fw-semibold mb-1">Avaliação de Desempenho:</div>
              <ul class="mb-3">
                <li v-for="(p, i) in preview.resumo" :key="i">
                  <strong>{{ p.pergunta }}:</strong> {{ p.resposta }}
                </li>
              </ul>

              <!-- Rodapé vindo do 'Documentos' -->
              <div class="mt-2" v-safe-html="preview.rodape_html"></div>

              <!-- (opcional) debug -->
              <!--
              <details class="mt-2">
                <summary class="fw-semibold">JSON completo (debug)</summary>
                <pre class="mb-0" style="white-space: pre-wrap;">{{ preview.respostas }}</pre>
              </details>
              -->

            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-outline-secondary btn-sm" @click="showPreview = false">Fechar</button>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body" v-if="batchOpen">
    <div class="batch-overlay" @click.self="/* não fecha ao clicar fora */null">
      <div class="batch-container">
        <div class="batch-header">
          <h6 class="mb-0">Enviando avaliações</h6>
        </div>

        <div class="batch-body">
          <div class="mb-2">
            <span class="mono">Enviando {{ Math.min(batchDone + 1, batchTotal) }} de {{ batchTotal }}</span>
          </div>

          <div class="progress mb-3">
            <div class="progress-bar" role="progressbar"
                :style="{ width: batchPercent + '%' }"
                :aria-valuenow="batchPercent" aria-valuemin="0" aria-valuemax="100">
              {{ batchPercent }}%
            </div>
          </div>

          <div v-if="batchFinished" class="mt-2">
            <div><strong>Concluído.</strong></div>
            <div class="small text-muted">
              Sucesso: {{ batchSummary.ok.length }} • Ignorados: {{ batchSummary.skipped.length }} • Falhas: {{ batchSummary.fail.length }}
            </div>

            <div v-if="batchSummary.skipped.length" class="mt-2">
              <div class="fw-semibold">Ignorados</div>
              <pre class="batch-pre">{{ formatReasons(batchSummary.reasons, batchSummary.skipped) }}</pre>
            </div>
            <div v-if="batchSummary.fail.length" class="mt-2">
              <div class="fw-semibold">Falhas</div>
              <pre class="batch-pre">{{ formatReasons(batchSummary.reasons, batchSummary.fail) }}</pre>
            </div>
          </div>
        </div>

        <div class="batch-footer d-flex justify-content-end gap-2">
          <button class="btn btn-outline-secondary btn-sm"
                  v-if="!batchFinished"
                  @click="batchCancel = true" :disabled="batchCancel">
            Cancelar
          </button>
          <button class="btn btn-primary btn-sm"
                  :disabled="!batchFinished"
                  @click="closeBatch">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <EmailAvaliacaoModal
    :show="showEmailModal"
    :resposta-id="modalRespostaId ?? undefined"
    @close="showEmailModal = false"
    @enviado="onEmailEnviado"
  />

</template>

<script>
import axios from '@/services/axios'
import globalUtils from '@/utils/globalUtils.js'
import EmailAvaliacaoModal from '@/components/modals/EmailAvaliacaoModal.vue'

export default {
  name: 'CardEnvioAvaliacoes',
  props: {
    canEnviar: { type: Boolean, default: false },
    perPage: { type: Number, default: 10 },
  },
  components: { EmailAvaliacaoModal },   // << adicione isto
  data () {
    return {
      total: 0,
      limit: this.perPage,
      offset: 0,
      ordenacao: 'data_realizacao_desc',
      rows: [],
      loading: false,
      error: null,
      selecionados: [],
      loteTamanho: 0,
      showPreview: false,
      previewLoading: false,
      previewError: null,
      preview: null,
      showEmailModal: false,
      modalRespostaId: null,
      sendingBatch: false,
      selectedSet: new Set(),         // seleção persistente entre páginas
      batchOpen: false,
      batchCancel: false,
      batchTotal: 0,
      batchDone: 0,
      batchSummary: { ok: [], skipped: [], fail: [], reasons: {} },
    }
  },
  computed: {
    paginaAtual () {
      return Math.floor(this.offset / this.limit) + 1
    },
    totalPaginas () {
      if (!this.total) return 1
      return Math.max(1, Math.ceil(this.total / this.limit))
    },
    permissaoEnviar () {
      const has = typeof this.hasPermission === 'function'
        ? this.hasPermission('enviar_avaliacoes')
        : null
      return has !== null ? has : this.canEnviar
    },

    selectedCount () { return this.selectedSet.size },
    allChecked () {
      return this.rows.length > 0 && this.rows.every(r => this.selectedSet.has(r.resposta_id))
    },

    batchPercent () {
      return this.batchTotal ? Math.round((this.batchDone * 100) / this.batchTotal) : 0
    },
    batchFinished () {
      return this.batchCancel || (this.batchDone >= this.batchTotal)
    },

  },
  methods: {
    async carregar () {
      this.loading = true
      this.error = null
      try {
        const params = { limit: this.limit, offset: this.offset, ordenacao: this.ordenacao }
        const { data } = await axios.get('/dashboard/pendentes-envio', { params })
        this.total     = Number(data?.total || 0)
        this.limit     = Number(data?.limit || this.limit)
        this.offset    = Number(data?.offset || this.offset)
        this.ordenacao = data?.ordenacao || this.ordenacao
        this.rows      = Array.isArray(data?.data) ? data.data : []
        this.selecionados = []
      } catch (e) {
        console.error('Erro carregar pendentes-envio:', e)
        this.error = 'Falha ao carregar pendências de envio.'
      } finally {
        this.loading = false
      }
    },
    recarregar () {
      this.offset = (this.paginaAtual - 1) * this.limit
      this.carregar()
    },
    paginaAnterior () {
      if (this.paginaAtual <= 1) return
      this.offset = (this.paginaAtual - 2) * this.limit
      this.carregar()
    },
    proximaPagina () {
      if (this.paginaAtual >= this.totalPaginas) return
      this.offset = this.paginaAtual * this.limit
      this.carregar()
    },
    toggleAll (ev) {
      if (ev.target.checked) {
        this.selecionados = this.rows.map(r => r.resposta_id)
      } else {
        this.selecionados = []
      }
    },
    fmtDate (iso) {
      return globalUtils?.formatarData ? globalUtils.formatarData(iso) : (iso || '')
    },
    preVisualizar (row) {
      this.modalRespostaId = row.resposta_id
      this.showEmailModal = true
    },
    async onEmailEnviado (respostaId) {
      // feedback instantâneo
      this.rows = this.rows.filter(r => r.resposta_id !== respostaId)
      this.total = Math.max(0, this.total - 1)
      // recarrega a página para “preencher” a lista (puxa próximos itens)
      await this.recarregar()
    },

    isSelected (id) { return this.selectedSet.has(id) },
    toggleSelected (id, checked) {
      if (checked) this.selectedSet.add(id)
      else this.selectedSet.delete(id)
    },
    toggleAllPage (ev) {
      const check = ev.target.checked
      for (const r of this.rows) {
        if (check) this.selectedSet.add(r.resposta_id)
        else this.selectedSet.delete(r.resposta_id)
      }
    },

    // ——— Lote mudou: clampa e auto-seleciona os primeiros N pendentes ———
    async onChangeLote () {
      let n = Number(this.loteTamanho)
      if (!Number.isFinite(n)) n = 0
      n = Math.max(0, Math.min(n, 30))
      this.loteTamanho = n

      // zera seleção atual e, se n>0, marca os N primeiros (atravessa páginas)
      this.selectedSet.clear()
      if (n > 0) {
        const ids = await this.gatherFirstNIds(n)
        ids.forEach(id => this.selectedSet.add(id))
      }
    },

    // ——— busca os primeiros N IDs pendentes, atravessando páginas ———
    async gatherFirstNIds (n) {
      const perPage = Number(this.limit || 10)
      const ord     = this.ordenacao || 'data_realizacao_desc'
      const ids = []
      let total = Infinity, offset = 0

      while (ids.length < n && offset < total) {
        const { data } = await axios.get('/dashboard/pendentes-envio', {
          params: { limit: perPage, offset, ordenacao: ord }
        })
        total = Number(data?.total || 0)
        const chunk = Array.isArray(data?.data) ? data.data : []
        for (const r of chunk) {
          if (ids.length >= n) break
          const id = r?.resposta_id
          if (Number.isFinite(id) && !ids.includes(id)) ids.push(id)
        }
        offset += perPage
        if (offset >= total) break
      }
      return ids
    },

    formatReasons (reasons = {}, ids = []) {
      if (!Array.isArray(ids) || !ids.length) return ''
      return ids.map(id => {
        const msg = reasons?.[id] || 'sem motivo informado'
        return `• #${id}: ${msg}`
      }).join('\n')
    },

    closeBatch () {
      this.batchOpen = false
      this.batchCancel = false
      this.batchTotal = 0
      this.batchDone = 0
      this.batchSummary = { ok: [], skipped: [], fail: [], reasons: {} }
      this.sendingBatch = false
    },

    async enviarIndividual (row) {
      if (!this.permissaoEnviar) return
      try {
        const id = row.resposta_id
        const { data } = await axios.post('/operacional/emails/enviar-avaliacao', {
          respostaIds: [id]
        })

        // Sucesso
        if (Array.isArray(data.ok) && data.ok.includes(id)) {
          this.rows = this.rows.filter(r => r.resposta_id !== id)
          this.total = Math.max(0, this.total - 1)
          await this.recarregar()           // << ADICIONE
          return
        }

        // Não foi OK → tente explicar o motivo
        const failMsg    = (Array.isArray(data.fail)    && data.fail.includes(id))    ? this.formatReasons(data.reasons, [id]) : ''
        const skippedMsg = (Array.isArray(data.skipped) && data.skipped.includes(id)) ? this.formatReasons(data.reasons, [id]) : ''

        if (failMsg) {
          alert(`Falha ao enviar:\n${failMsg}`)
        } else if (skippedMsg) {
          alert(`Envio ignorado:\n${skippedMsg}`)
        } else {
          alert('Não foi possível enviar este registro agora.')
        }
      } catch (e) {
        console.error('Enviar individual erro:', e)
        alert('Falha ao enviar.')
      }
    },

    async enviarLote () {
      if (!this.permissaoEnviar || this.selectedCount === 0 || this.sendingBatch) return
      const fila = Array.from(this.selectedSet)

      // progresso
      this.sendingBatch = true
      this.batchOpen = true
      this.batchCancel = false
      this.batchTotal = fila.length
      this.batchDone = 0
      this.batchSummary = { ok: [], skipped: [], fail: [], reasons: {} }

      for (const id of fila) {
        if (this.batchCancel) break
        try {
          const { data } = await axios.post('/operacional/emails/enviar-avaliacao', { respostaIds: [id] })
          if (Array.isArray(data.ok) && data.ok.includes(id)) this.batchSummary.ok.push(id)
          if (Array.isArray(data.skipped) && data.skipped.includes(id)) this.batchSummary.skipped.push(id)
          if (Array.isArray(data.fail) && data.fail.includes(id)) this.batchSummary.fail.push(id)
          if (data.reasons) Object.assign(this.batchSummary.reasons, data.reasons)
        } catch (e) {
          console.error('Falha ao enviar id=', id, e)
          this.batchSummary.fail.push(id)
          this.batchSummary.reasons[id] = 'erro de rede/servidor'
        } finally {
          this.batchDone++
        }
      }

      // remove OKs localmente e da seleção
      if (this.batchSummary.ok.length) {
        const okSet = new Set(this.batchSummary.ok)
        this.rows = this.rows.filter(r => !okSet.has(r.resposta_id))
        this.total = Math.max(0, this.total - this.batchSummary.ok.length)
        for (const id of okSet) this.selectedSet.delete(id)
      }

      // paginação coerente + refresh para preencher a página
      if (this.rows.length === 0 && this.total > 0 && this.paginaAtual > 1) {
        this.paginaAnterior()
      }
      await this.recarregar()

      this.sendingBatch = false
    },
  },
  mounted () {
    this.carregar()
  }
}
</script>

<style scoped>
.dashboard-card h6 {
  font-weight: 600;
}

/* Magenta */
.badge-magenta {
  background-color: #d63384;
  color: #fff;
  font-weight: 600;
  border-radius: 999px;
}
.btn-outline-magenta {
  color: #d63384;
  border-color: #d63384;
}
.btn-outline-magenta:hover,
.btn-outline-magenta:focus {
  color: #fff;
  background-color: #d63384;
  border-color: #d63384;
}

/* Ícones compactos */
.btn-icon {
  padding: .25rem .45rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Checkbox menor */
.chk-sm {
  width: 14px;
  height: 14px;
  transform: scale(0.9);
  transform-origin: center;
  vertical-align: middle;
}

/* monospace para matrícula */
.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

/* Truncamento elegante */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* util gap (só no header) */
.gap-2 > * + * { margin-left: .5rem; }
/* Ações com ícones “soltos” (sem botões) */
.actions .action-icon {
  font-size: 1.05rem;
  margin: 0 .35rem;
  cursor: pointer;
  vertical-align: middle;
}

.actions .action-gray   { color: #d63384; }   /* pré-visualizar */
.actions .action-magenta{ color: #d63384; }   /* enviar */

.actions .action-icon:hover {
  opacity: .85;
}
.actions .action-icon.disabled {
  opacity: .35;
  pointer-events: none;
  cursor: default;
}

/* Modal light (mesmo padrão dos seus outros modais) */
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.35);
  display: flex; align-items: center; justify-content: center; z-index: 1050;
}
.modal-container {
  width: clamp(420px, 80vw, 900px);
  background: #fff; border-radius: .5rem; overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,.25);
}
.modal-header, .modal-footer { padding: .6rem .8rem; background: #f8f9fa; }
.modal-body { padding: .8rem; }

.batch-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.5);
  display: flex; align-items: center; justify-content: center; z-index: 1060;
}
.batch-container {
  background: #fff; width: clamp(420px, 70vw, 720px);
  max-height: 80vh; display: flex; flex-direction: column;
  border-radius: 8px; overflow: hidden; box-shadow: 0 10px 30px rgba(0,0,0,.25);
}
.batch-header, .batch-footer { background: #f8f9fa; padding: 10px 12px; flex: 0 0 auto; }
.batch-body { padding: 12px; flex: 1 1 auto; overflow: auto; }
.batch-pre {
  background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 6px;
  padding: 8px; margin: 4px 0 0 0; white-space: pre-wrap;
}

/* Caso não use Bootstrap, mínima barra de progresso */
.progress { height: 12px; background: #e9ecef; border-radius: 6px; overflow: hidden; }
.progress-bar { background: #0d6efd; color: #fff; font-size: 12px; line-height: 12px; text-align: center; }

/* Faixa do cabeçalho na cor #9B59B6 */
.header-amethyst {
  background-color: #9B59B6;
  color: #fff;
}

/* Badge com bom contraste sobre o roxo */
.badge-on-amethyst {
  background: #fff;
  color: #9B59B6;
  font-weight: 700;
  border-radius: 999px;
}

/* Controles “claros” sobre o header roxo */
.header-amethyst .input-group-text {
  background: rgba(255,255,255,.18);
  color: #fff;
  border-color: rgba(255,255,255,.35);
}
.header-amethyst .form-control {
  background: rgba(255,255,255,.08);
  color: #fff;
  border-color: rgba(255,255,255,.35);
}
.header-amethyst .form-control:focus {
  background: rgba(255,255,255,.18);
  color: #fff;
  border-color: #fff;
  box-shadow: 0 0 0 .2rem rgba(255,255,255,.25);
}

/* Botão outline branco para header escuro */
.btn-outline-white {
  color: #fff;
  border-color: #fff;
}
.btn-outline-white:hover,
.btn-outline-white:focus {
  color: #9B59B6;
  background: #fff;
  border-color: #fff;
}
.btn-outline-white .spinner-border {
  color: currentColor;
}

/* Deixe os controles do header exatamente com a mesma altura */
.header-amethyst .header-controls {
  --ctrl-h: 28px;                 /* ajuste fino aqui (32/34/36) */
}

.header-amethyst .header-controls .input-group-sm .input-group-text,
.header-amethyst .header-controls .input-group-sm .form-control,
.header-amethyst .header-controls .btn-sm {
  height: var(--ctrl-h);
  line-height: calc(var(--ctrl-h) - 2px);  /* centraliza texto com borda */
  padding-top: 0; padding-bottom: 0;
  box-sizing: border-box;
}

/* (opcional) arredondamento igual */
.header-amethyst .header-controls .input-group-sm .input-group-text,
.header-amethyst .header-controls .input-group-sm .form-control,
.header-amethyst .header-controls .btn-sm {
  border-radius: .2rem;
}
/* Base da faixa de paginação — altura menor */
.card-pagination {
  padding: 4px 8px;           /* reduz a altura vertical */
}

/* Texto na cor #9B59B6 */
.pagination-text { color: #9B59B6; }

/* Botão outline roxo #9B59B6 */
.btn-outline-amethyst {
  color: #9B59B6;
  border-color: #9B59B6;
  background: transparent;
}
.btn-outline-amethyst:hover,
.btn-outline-amethyst:focus {
  color: #fff;
  background: #9B59B6;
  border-color: #9B59B6;
}

/* Versão compacta (mesma altura dos dois botões) */
/* Botões da paginação: altura baixa + conteúdo centralizado */
.btn-compact{
  height: 24px;                 /* ajuste fino: 22–26px */
  min-width: 34px;
  padding: 4 2rem;
  display: inline-flex;         /* centraliza vertical e horizontal */
  align-items: center;
  justify-content: center;
  line-height: 1;               /* não “engorda” o botão */
  border-radius: .2rem;

}

/* Estado desabilitado mantendo a cor */
.btn-outline-amethyst:disabled {
  color: #9B59B6;
  border-color: #9B59B6;
  opacity: .45;
  background: transparent;
}

</style>
